import { ColumnDef, ColumnHelper, createColumnHelper } from "@tanstack/react-table";
import { SystemStyleObject } from "@chakra-ui/react";
import { ReactNode } from "react";

/**
 * Base options for creating table columns with smart defaults
 */
interface BaseColumnOptions<T, TValue> {
  /** Column ID (optional, uses accessor key if not provided) */
  id?: string;

  /** Column header text */
  header?: string;

  /** Custom cell renderer */
  cell?: (props: { getValue: () => TValue; row: { original: T } }) => ReactNode;

  /** Enable sorting (default: false) */
  enableSorting?: boolean;

  /** Fixed width in pixels - sets width, maxWidth, and minWidth to the same value */
  fixedWidth?: number;

  /** Flexible width with min/max constraints */
  flexWidth?: {
    min?: number;
    max?: number;
    size?: number;
  };

  /** Custom styles for the cell */
  sx?: SystemStyleObject;

  /** Padding preset (removes padding-left, padding-right, or both) */
  padding?: "none" | "left-only" | "right-only";

  /** Horizontal alignment for header and cell */
  align?: "flex-start" | "center" | "flex-end";

  /** Additional meta properties to merge */
  meta?: Record<string, any>;
}

/**
 * Infer the value type based on the accessor type
 */
type InferAccessorValue<T, A> = A extends keyof T
  ? T[A]
  : A extends (row: T) => infer R
    ? R
    : T;

/**
 * Column options - generic A will be inferred from the accessor value
 */
export type ColumnOptions<T, A = undefined> = BaseColumnOptions<T, InferAccessorValue<T, A>> & {
  accessor?: A;
};

/**
 * Creates a column definition with smart defaults and common patterns
 *
 * @example Basic usage with accessor
 * ```ts
 * createColumn(columnHelper, "title", {
 *   header: "Name",
 *   enableSorting: true,
 * })
 * ```
 *
 * @example Fixed width column
 * ```ts
 * createColumn(columnHelper, (row) => row, {
 *   header: "Stage",
 *   fixedWidth: 110,
 * })
 * ```
 *
 * @example Flexible width with alignment
 * ```ts
 * createColumn(columnHelper, "name", {
 *   header: "Name",
 *   flexWidth: { min: 100, max: 400 },
 *   align: "flex-start",
 * })
 * ```
 */
export function createColumn<T, TValue = any>(
  columnHelper: ColumnHelper<T>,
  accessor: keyof T | ((row: T) => TValue),
  options: ColumnOptions<T> = {},
): ColumnDef<T, any> {
  const {
    header = "",
    enableSorting = false,
    fixedWidth,
    flexWidth,
    sx = {},
    padding,
    align,
    meta = {},
  } = options;

  // Build size configuration
  const sizeConfig: Partial<ColumnDef<T, any>> = {};

  if (fixedWidth !== undefined) {
    // Fixed width: set all three size properties to the same value
    sizeConfig.size = fixedWidth;
    sizeConfig.minSize = fixedWidth;
    sizeConfig.maxSize = fixedWidth;
  } else if (flexWidth) {
    // Flexible width: set individual min/max/size values
    if (flexWidth.min !== undefined) sizeConfig.minSize = flexWidth.min;
    if (flexWidth.max !== undefined) sizeConfig.maxSize = flexWidth.max;
    if (flexWidth.size !== undefined) sizeConfig.size = flexWidth.size;
  }

  // Build sx styles
  const computedSx: SystemStyleObject = { ...sx };

  // Apply padding presets
  if (padding === "none") {
    computedSx.paddingLeft = 0;
    computedSx.paddingRight = 0;
  } else if (padding === "left-only") {
    computedSx.paddingRight = 0;
  } else if (padding === "right-only") {
    computedSx.paddingLeft = 0;
  }

  // Apply fixed width to sx if provided (for compatibility with some table implementations)
  if (fixedWidth !== undefined) {
    computedSx.width = `${fixedWidth}px`;
    computedSx.maxWidth = `${fixedWidth}px`;
    computedSx.minWidth = `${fixedWidth}px`;
  }

  // Build meta object
  const computedMeta: Record<string, any> = { ...meta };

  if (Object.keys(computedSx).length > 0) {
    computedMeta.sx = { ...computedSx, ...meta.sx };
  }

  // Apply alignment
  if (align) {
    computedMeta.headerHStackProps = { justifyContent: align, ...meta.headerHStackProps };
    computedMeta.cellHStackProps = { justifyContent: align, ...meta.cellHStackProps };
  }

  // Create the column using columnHelper
  const baseColumn = typeof accessor === "function"
    ? columnHelper.accessor(accessor as any, { header })
    : columnHelper.accessor(accessor as any, { header });

  return {
    ...baseColumn,
    enableSorting,
    ...sizeConfig,
    ...(Object.keys(computedMeta).length > 0 ? { meta: computedMeta } : {}),
  };
}

/**
 * Creates a spacer column (empty column that fills remaining space)
 *
 * @example
 * ```ts
 * createSpacerColumn(columnHelper)
 * ```
 */
export function createSpacerColumn<T>(columnHelper: ColumnHelper<T>): ColumnDef<T, any> {
  return createColumn(columnHelper, (row) => row, {
    header: "",
    padding: "none",
    flexWidth: { min: 0 },
    meta: {
      sx: {
        paddingLeft: 0,
        paddingRight: 0,
      },
    },
  });
}

/**
 * Creates an action column (typically for buttons/menus at the end of a row)
 *
 * @example
 * ```ts
 * createActionColumn(columnHelper, {
 *   fixedWidth: 99,
 *   align: "flex-end",
 * })
 * ```
 */
export function createActionColumn<T>(
  columnHelper: ColumnHelper<T>,
  options: Omit<ColumnOptions<T>, "header"> = {},
): ColumnDef<T, any> {
  return createColumn(columnHelper, (row) => row, {
    header: "",
    align: "flex-end",
    ...options,
  });
}

/**
 * Creates a wrapped column helper with smart defaults built-in.
 * This is the preferred API - no need to pass columnHelper around or spread results.
 *
 * Type inference works automatically! Inspired by TanStack Table's API pattern,
 * the accessor is a positional parameter which allows TypeScript to preserve
 * literal types and infer the correct return type for getValue().
 *
 * @example Basic usage
 * ```ts
 * interface Task {
 *   id: string;
 *   title: string;
 *   stageKey?: string;
 * }
 *
 * const column = createColumnHelpers<Task>();
 *
 * const columns = [
 *   // String accessor - type automatically inferred! ✓
 *   column.accessor("title", {
 *     header: "Title",
 *     cell: ({ getValue }) => <Text>{getValue()}</Text>, // getValue(): string (automatic!)
 *   }),
 *
 *   // Function accessor - type inferred from function return ✓
 *   column.accessor((row) => row.id, {
 *     header: "ID",
 *     cell: ({ getValue }) => <Text>{getValue()}</Text>, // getValue(): string (inferred!)
 *   }),
 *
 *   // No accessor - getValue() returns entire row ✓
 *   column.create({
 *     id: "stage",
 *     header: "Stage",
 *     fixedWidth: 110,
 *     align: "flex-start",
 *     cell: ({ getValue }) => {
 *       const task = getValue(); // task: Task (inferred!)
 *       return <StageBadge stageId={task.stageKey} />;
 *     },
 *   }),
 *
 *   column.spacer(),
 *
 *   column.action({
 *     id: "menu",
 *     fixedWidth: 99,
 *     cell: ({ getValue }) => <ThreeDotMenu item={getValue()} />, // getValue(): Task
 *   }),
 * ];
 * ```
 */
export function createColumnHelpers<T>() {
  const columnHelper = createColumnHelper<T>();

  return {
    /**
     * Creates a column with an accessor (string key or function)
     *
     * Type inference works automatically without explicit type parameters:
     * - String accessor: column.accessor("name", { ... }) → getValue(): string ✓
     * - Function accessor: column.accessor((row) => row.id, { ... }) → getValue(): string ✓
     *
     * @param accessorParam - Field key or accessor function
     * @param options - Column configuration options
     */
    accessor<K extends keyof T, TValue = T[K]>(
      accessorParam: K | ((row: T) => TValue),
      options: Omit<BaseColumnOptions<T, TValue>, "accessor"> = {},
    ): ColumnDef<T, any> {
      const {
        id,
        header = "",
        cell,
        enableSorting = false,
        fixedWidth,
        flexWidth,
        sx = {},
        padding,
        align,
        meta = {},
      } = options;

      // Build size configuration
      const sizeConfig: Partial<ColumnDef<T, any>> = {};

      if (fixedWidth !== undefined) {
        sizeConfig.size = fixedWidth;
        sizeConfig.minSize = fixedWidth;
        sizeConfig.maxSize = fixedWidth;
      } else if (flexWidth) {
        if (flexWidth.min !== undefined) sizeConfig.minSize = flexWidth.min;
        if (flexWidth.max !== undefined) sizeConfig.maxSize = flexWidth.max;
        if (flexWidth.size !== undefined) sizeConfig.size = flexWidth.size;
      }

      // Build sx styles
      const computedSx: SystemStyleObject = { ...sx };

      // Apply padding presets
      if (padding === "none") {
        computedSx.paddingLeft = 0;
        computedSx.paddingRight = 0;
      } else if (padding === "left-only") {
        computedSx.paddingRight = 0;
      } else if (padding === "right-only") {
        computedSx.paddingLeft = 0;
      }

      // Apply fixed width to sx if provided
      if (fixedWidth !== undefined) {
        computedSx.width = `${fixedWidth}px`;
        computedSx.maxWidth = `${fixedWidth}px`;
        computedSx.minWidth = `${fixedWidth}px`;
      }

      // Build meta object
      const computedMeta: Record<string, any> = { ...meta };

      if (Object.keys(computedSx).length > 0) {
        computedMeta.sx = { ...computedSx, ...meta.sx };
      }

      // Apply alignment
      if (align) {
        computedMeta.headerHStackProps = { justifyContent: align, ...meta.headerHStackProps };
        computedMeta.cellHStackProps = { justifyContent: align, ...meta.cellHStackProps };
      }

      // Create the base column using the accessor parameter
      const baseColumn = typeof accessorParam === "function"
        ? columnHelper.accessor(accessorParam as any, { header, ...(cell ? { cell } : {}) })
        : columnHelper.accessor(accessorParam as any, { header, ...(cell ? { cell } : {}) });

      return {
        ...baseColumn,
        ...(id ? { id } : {}),
        enableSorting,
        ...sizeConfig,
        ...(Object.keys(computedMeta).length > 0 ? { meta: computedMeta } : {}),
      };
    },

    /**
     * Creates a column without an accessor (returns entire row)
     *
     * @param options - Column configuration options
     */
    create(options: Omit<BaseColumnOptions<T, T>, "accessor"> = {}): ColumnDef<T, any> {
      return this.accessor((row) => row, options);
    },

    /**
     * Creates a spacer column (empty column that fills remaining space)
     */
    spacer(): ColumnDef<T, any> {
      return this.create({
        id: "spacer",
        header: "",
        padding: "none",
        flexWidth: { min: 0 },
        cell: () => null,
      });
    },

    /**
     * Creates an action column (typically for buttons/menus at the end of a row)
     */
    action(options: Omit<BaseColumnOptions<T, T>, "accessor"> = {}): ColumnDef<T, any> {
      return this.create({
        header: "",
        align: "flex-end",
        ...options,
      });
    },
  };
}
